import { z } from 'zod';

import { IdSchema, IsoDateSchema } from './common.js';

// ─── Enum schemas ─────────────────────────────────────────────────────────

// Ported verbatim from main:lib/ai.ts (the friend's prototype). Order and
// spelling match the source-of-truth muscle list used by main's prompt at
// line 304. Do not reorder or rename — main's exercise library and the AI's
// prompt rules both depend on these exact strings.
export const MuscleNameEnum = z.enum([
  'Chest',
  'Upper Chest',
  'Lower Chest',
  'Lats',
  'Mid Back',
  'Lower Back',
  'Traps',
  'Rear Deltoid',
  'Front Deltoid',
  'Side Deltoid',
  'Rotator Cuff',
  'Biceps',
  'Brachialis',
  'Triceps',
  'Forearms',
  'Abs',
  'Lower Abs',
  'Obliques',
  'Core',
  'Hip Flexors',
  'Quads',
  'Hamstrings',
  'Glutes',
  'Inner Thighs',
  'Calves',
  'Soleus',
  'Cardiovascular System',
]);
export type MuscleName = z.infer<typeof MuscleNameEnum>;

// Ported from main:lib/ai.ts (line 296). Single deviation: main uses
// "full body" (space); we normalize to "full_body" (underscore) for enum
// cleanliness.
export const SessionTypeEnum = z.enum([
  'push',
  'pull',
  'legs',
  'upper',
  'lower',
  'full_body',
  'core',
  'cardio',
  'hiit',
  'strength',
  'flexibility',
  'sport',
]);
export type SessionType = z.infer<typeof SessionTypeEnum>;

export const PlanStatusEnum = z.enum(['draft', 'active', 'archived']);
export type PlanStatus = z.infer<typeof PlanStatusEnum>;

// Extended in M4 to include 'nutrition'. Per-endpoint response schemas narrow
// `type` to a literal so a /v1/ai/plan response can never serialize as
// 'nutrition' and a /v1/ai/diet response can never serialize as 'training'.
export const PlanTypeEnum = z.enum(['training', 'nutrition']);
export type PlanType = z.infer<typeof PlanTypeEnum>;

// ─── Plan content (versioned JSON shape) ──────────────────────────────────

export const WorkoutExerciseV1Schema = z.object({
  id: IdSchema,
  // Catalog identity for grounded plans. Optional only so pre-M9 V1 rows
  // remain readable; all current routine-recommendation writes require it.
  exercise_id: z.string().min(1).max(128).optional(),
  exercise_name: z.string().min(1).max(120),
  sets: z.number().int().min(1).max(10),
  reps: z.string().min(1).max(30),
  rest_seconds: z.number().int().min(0).max(600),
  muscle_group: z.string().min(1).max(50),
  primary_muscles: z.array(MuscleNameEnum).min(1).max(5),
  secondary_muscles: z.array(MuscleNameEnum).max(5).default([]),
  notes: z.string().max(500).default(''),
});
export type WorkoutExerciseV1 = z.infer<typeof WorkoutExerciseV1Schema>;

export const WorkoutSessionV1Schema = z.object({
  id: IdSchema,
  // 0 = Sunday, 1 = Monday, … 6 = Saturday. Matches main:lib/ai.ts line 295.
  day_of_week: z.number().int().min(0).max(6),
  session_name: z.string().min(1).max(100),
  session_type: SessionTypeEnum,
  duration_minutes: z.number().int().min(15).max(180),
  exercises: z.array(WorkoutExerciseV1Schema).min(1).max(10),
});
export type WorkoutSessionV1 = z.infer<typeof WorkoutSessionV1Schema>;

export const PlanContentV1Schema = z
  .object({
    version: z.literal('v1'),
    // One entry per training day. Rest days are absences, not entries.
    sessions: z.array(WorkoutSessionV1Schema).min(1).max(7),
  })
  .superRefine((content, ctx) => {
    const sessionIds = new Set<string>();
    const exerciseIds = new Set<string>();

    content.sessions.forEach((session, sessionIndex) => {
      if (sessionIds.has(session.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sessions', sessionIndex, 'id'],
          message: 'session IDs must be unique within a plan',
        });
      }
      sessionIds.add(session.id);

      session.exercises.forEach((exercise, exerciseIndex) => {
        if (exerciseIds.has(exercise.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sessions', sessionIndex, 'exercises', exerciseIndex, 'id'],
            message: 'exercise instance IDs must be unique within a plan',
          });
        }
        exerciseIds.add(exercise.id);
      });
    });
  });
export type PlanContentV1 = z.infer<typeof PlanContentV1Schema>;

// ─── Plan response shape ──────────────────────────────────────────────────

export const PlanResponseSchema = z.object({
  id: IdSchema,
  userId: IdSchema,
  type: z.literal('training'),
  status: PlanStatusEnum,
  startsOn: IsoDateSchema,
  endsOn: IsoDateSchema,
  content: PlanContentV1Schema,
  modelUsed: z.string(),
  promptVersion: z.string(),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
});
export type PlanResponse = z.infer<typeof PlanResponseSchema>;

// ─── Request / response DTOs ──────────────────────────────────────────────

export const GeneratePlanRequestSchema = z.object({});
export type GeneratePlanRequest = z.infer<typeof GeneratePlanRequestSchema>;

export const GeneratePlanResponseSchema = z.object({
  plan: PlanResponseSchema,
});
export type GeneratePlanResponse = z.infer<typeof GeneratePlanResponseSchema>;

export const GetPlanResponseSchema = z.object({
  plan: PlanResponseSchema.nullable(),
});
export type GetPlanResponse = z.infer<typeof GetPlanResponseSchema>;

export const AcceptPlanRequestSchema = z.object({
  planId: IdSchema,
});
export type AcceptPlanRequest = z.infer<typeof AcceptPlanRequestSchema>;

export const AcceptPlanResponseSchema = z.object({
  plan: PlanResponseSchema,
});
export type AcceptPlanResponse = z.infer<typeof AcceptPlanResponseSchema>;

// ─── PATCH /v1/ai/plan/session/:sessionId ─────────────────────────────────
//
// Replaces the full exercise list of one session in the user's draft OR
// active training plan (ADR-0016: active plans are editable). The wire
// shape carries one of two per-exercise variants:
//   - existing exercise: { id } + optional sets/reps/rest/notes overrides
//     (id matches a current exercise in the session — fine-grained
//     muscle metadata is preserved from the AI-generated original)
//   - new exercise:      { exerciseId, sets, reps, rest_seconds } where
//     `exerciseId` references a row in the Exercise catalog. The server
//     resolves name + muscle_group from the catalog and assigns a fresh
//     server-side UUID. Free-text exercises are not accepted — every add
//     must come from the catalog so weekly review + future logging can
//     reason about the muscle coverage.
export const UpdateSessionExerciseExistingSchema = z.object({
  kind: z.literal('existing'),
  id: IdSchema,
  sets: z.number().int().min(1).max(10).optional(),
  reps: z.string().min(1).max(30).optional(),
  rest_seconds: z.number().int().min(0).max(600).optional(),
  notes: z.string().max(500).optional(),
});
export type UpdateSessionExerciseExisting = z.infer<typeof UpdateSessionExerciseExistingSchema>;

export const UpdateSessionExerciseNewSchema = z.object({
  kind: z.literal('new'),
  exerciseId: z.string().min(1).max(128),
  sets: z.number().int().min(1).max(10),
  reps: z.string().min(1).max(30),
  rest_seconds: z.number().int().min(0).max(600),
  notes: z.string().max(500).optional(),
});
export type UpdateSessionExerciseNew = z.infer<typeof UpdateSessionExerciseNewSchema>;

export const UpdateSessionExerciseSchema = z.discriminatedUnion('kind', [
  UpdateSessionExerciseExistingSchema,
  UpdateSessionExerciseNewSchema,
]);
export type UpdateSessionExercise = z.infer<typeof UpdateSessionExerciseSchema>;

export const UpdateSessionRequestSchema = z.object({
  exercises: z.array(UpdateSessionExerciseSchema).min(1).max(10),
});
export type UpdateSessionRequest = z.infer<typeof UpdateSessionRequestSchema>;

export const UpdateSessionResponseSchema = z.object({
  plan: PlanResponseSchema,
});
export type UpdateSessionResponse = z.infer<typeof UpdateSessionResponseSchema>;
